import {
  WebsiteButton,
  WebsiteCard,
  WebsiteContainer,
  WebsiteFooter,
  WebsiteFormField,
  WebsiteInput,
  WebsitePage,
  WebsitePageHeader,
  WebsiteSection,
  WebsiteText,
  WebsiteTextarea,
} from "@-/dev.improve.haniverse.components.website";
import {
  ACCOUNT_DELETION_CONTACT_EMAIL,
  buildAccountDeletionMailtoUrl,
} from "@-/dev.improve.haniverse.lib.account-deletion";
import type { FormEvent } from "react";

const DELETE_ACCOUNT_TITLE = "Delete your account — Haniverse";
const DELETE_ACCOUNT_DESCRIPTION =
  "Request deletion of your Haniverse account and its synced study data.";

function readFormText(formData: FormData, name: string): string {
  const value = formData.get(name);
  return typeof value === "string" ? value : "";
}

function submitDeletionRequest(event: FormEvent<HTMLFormElement>) {
  event.preventDefault();
  const formData = new FormData(event.currentTarget);
  globalThis.location.assign(
    buildAccountDeletionMailtoUrl({
      email: readFormText(formData, "email"),
      message: readFormText(formData, "message"),
    }),
  );
}

/**
 * Public account deletion request page at `/deleteme`, rendered on web only — the URL the app
 * stores list for deleting an account. It drafts an email instead of calling the API, so it works
 * signed out and without the app's providers; we confirm with the account's address by reply.
 */
export function DeleteAccountRequestPage() {
  return (
    <WebsitePage description={DELETE_ACCOUNT_DESCRIPTION} title={DELETE_ACCOUNT_TITLE}>
      <WebsitePageHeader
        description="Submit a request to delete your Haniverse account and associated synced data. We will confirm the request by email before removing the account."
        eyebrow="Account"
        title="Delete your account"
      />
      <main>
        <WebsiteSection spacing="compact">
          <WebsiteContainer>
            <WebsiteCard className="max-w-[36rem]">
              <form className="flex flex-col gap-5" onSubmit={submitDeletionRequest}>
                <WebsiteFormField label="Account email">
                  <WebsiteInput autoComplete="email" name="email" required type="email" />
                </WebsiteFormField>
                <WebsiteFormField label="Additional details (optional)">
                  <WebsiteTextarea
                    name="message"
                    placeholder="Anything we should know before deleting your account"
                  />
                </WebsiteFormField>
                <WebsiteButton className="self-start" size="lg" type="submit">
                  Open email to send request
                </WebsiteButton>
                <WebsiteText size="caption">
                  This opens your email app with a draft to {ACCOUNT_DELETION_CONTACT_EMAIL}. You
                  can also write to that address directly.
                </WebsiteText>
              </form>
            </WebsiteCard>
          </WebsiteContainer>
        </WebsiteSection>
      </main>
      <WebsiteFooter />
    </WebsitePage>
  );
}
